import {
  WorkbenchAvailableAction,
  WorkbenchRow,
} from '../models/workbench-row.model'

/**
 * Per-row action gating from the read response's `availableActions`.
 *
 * Same mechanism as the 1.0 panels — `checkExtTxIncludesAvailableAction` and its `some` / `every`
 * wrappers in `libs/common-api/src/lib/utils/external-transactions-utils.ts`, which is what gates
 * Hold / Ignore / Unmatch in the external-transactions menu. Transactions 2.0 gates Match, Unmatch,
 * Delete and Ignore the same way.
 *
 * ONE deliberate difference from the 1.0 helpers: a row whose `availableActions` is `undefined` is
 * treated as ungated (the action stays enabled), where the 1.0 helper returns false and would
 * disable it. The 1.0 panels can assume the field is always populated; the flex read API does not
 * populate it on every path yet, and a missing field there would silently disable all four items
 * with no way for a user to tell why. An EMPTY array is NOT the same thing — that is the server
 * answering "nothing is permitted on this row", and it disables.
 */

/**
 * Whether one row permits an action.
 *
 * @param row the row in question
 * @param action the action being offered
 * @returns true when the server permits it, or when the server did not report on this row at all
 */
export function rowAllowsAction(
  row: WorkbenchRow,
  action: WorkbenchAvailableAction,
): boolean {
  const actions = row.availableActions
  if (actions === undefined) return true
  return actions.includes(action)
}

/**
 * Whether an action should be offered for a selection.
 *
 * `some`, not `every`, matching `shouldDisableActionOnlyIfAllFails` in the external-transactions
 * menu: a mixed selection keeps the item enabled and the rows that do not permit it are simply not
 * acted on. Disabling on the strictest row would make a large selection unusable because of one
 * member.
 *
 * An empty selection permits nothing — there is no row to act on.
 *
 * @param rows the rows the action would be scoped to
 * @param action the action being offered
 * @returns true when at least one row permits it
 */
export function someRowAllowsAction(
  rows: readonly WorkbenchRow[],
  action: WorkbenchAvailableAction,
): boolean {
  return rows.length > 0 && rows.some(row => rowAllowsAction(row, action))
}

/**
 * The subset of rows permitting an action.
 *
 * Provided for callers that need to narrow what they submit the way the 1.0 task service does
 * (`external-transactions-task.service.ts` filters before posting). The menu itself only needs
 * {@link someRowAllowsAction}.
 *
 * @param rows the rows the action would be scoped to
 * @param action the action being offered
 * @returns only the rows the server permits it on
 */
export function rowsAllowingAction(
  rows: readonly WorkbenchRow[],
  action: WorkbenchAvailableAction,
): WorkbenchRow[] {
  return rows.filter(row => rowAllowsAction(row, action))
}

/**
 * The tooltip for an item disabled purely because the server does not permit it.
 *
 * Worded as a permission statement rather than a rule the user broke: there is nothing they can
 * change about their selection to satisfy it. Rules they CAN satisfy are not pre-empted here at all
 * — they come back as a 400 and are rendered from the server's `validationId`.
 */
export const NOT_PERMITTED_REASON =
  'This action is not available for the selected transactions.'
